/**
 * Was fehlt einer Verordnung noch, damit sie abgerechnet werden kann?
 *
 * Der Chip „Unvollständig" (`documentStatus`) wird an mehreren Stellen gesetzt —
 * vom Scan bei Warnungen, von der Platzhalter-Verordnung einer Einsteiger-Tour,
 * per Hand im Formular. Er sagt aber nicht, *warum*. Diese Liste ist die
 * gemeinsame Antwort darauf, die Detailseite und Bearbeitungsformular
 * gleichermaßen benutzen — damit beide dieselben Felder benennen.
 *
 * Maßstab ist die Kassenabrechnung: Genau diese Angaben verlangt der
 * DMRZ-Fahrtenimport, ohne sie wird der Fall abgewiesen
 * (nextcab_mobile/docs/general/krankenfahrten_dmrz_abrechnung_konzept.md §4.3.3).
 */

export const ORDER_REQUIRED_FIELDS = [
  { field: 'patientName', label: 'Patient' },
  { field: 'patientBirthDate', label: 'Geburtsdatum' },
  { field: 'patientStreet', label: 'Straße (Anschrift des Versicherten)' },
  { field: 'patientZip', label: 'PLZ (Anschrift des Versicherten)' },
  { field: 'patientCity', label: 'Ort (Anschrift des Versicherten)' },
  { field: 'healthInsuranceName', label: 'Krankenkasse' },
  { field: 'healthInsuranceIk', label: 'Kostenträgerkennung (IK)' },
  { field: 'insuranceNumber', label: 'Versichertennummer' },
  { field: 'prescriptionDate', label: 'Ausstellungsdatum der Verordnung' },
  { field: 'validFrom', label: 'Gültig ab' },
  { field: 'validUntil', label: 'Gültig bis' }
];

/**
 * Werte kommen je nach Quelle als Firestore-Timestamp (Detailseite) oder als
 * String aus dem Formular — beides muss als „vorhanden" durchgehen.
 */
const isFilled = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return true;
  if (typeof value === 'object') {
    // Timestamp bzw. Date
    return typeof value.toDate === 'function' || value.seconds !== undefined
      || value instanceof Date;
  }
  return Boolean(value);
};

/** Ein IK hat genau neun Ziffern — eine achtstellige Zahl ist ein Lesefehler. */
const isValidIk = (value) => /^\d{9}$/.test(String(value ?? '').trim());

/**
 * Ist die Gültigkeit überschritten?
 *
 * Führend ist der tägliche Scheduler, der `documentStatus` serverseitig auf
 * „abgelaufen" setzt — er greift auch, wenn den Datensatz niemand öffnet. Diese
 * Prüfung hier ist die Sofortansicht im Portal: Sie zeigt den Ablauf schon am
 * Tag selbst und verhindert vor allem, dass die automatische Statusherleitung
 * eine abgelaufene Verordnung wieder auf „vollständig" zurückdreht.
 */
export const isOrderExpired = (order) => {
  const key = toComparableDate(order?.validUntil);
  if (!key) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return key < today;
};

const toComparableDate = (value) => {
  if (!value) return null;
  const date = typeof value?.toDate === 'function'
    ? value.toDate()
    : (value?.seconds ? new Date(value.seconds * 1000) : new Date(value));
  if (Number.isNaN(date?.getTime?.())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

/**
 * Braucht diese Verordnung eine Genehmigung der Krankenkasse?
 *
 * Ergibt sich aus dem angekreuzten Grund unter Punkt 1 (a–c genehmigungsfrei,
 * d–f pflichtig), den der Scan auswertet. Steht dort nichts, gilt die
 * Verordnung vorsichtshalber als genehmigungspflichtig.
 */
const needsApproval = (order) => (
  order?.approvalRequired !== false && order?.approvalStatus !== 'not_required'
);

/**
 * Liefert die fehlenden Pflichtangaben als `[{ field, label }]`.
 * Reihenfolge = Reihenfolge im Formular, damit die Liste beim Nachtragen
 * von oben nach unten abgearbeitet werden kann.
 */
export const getMissingOrderFields = (order) => {
  if (!order) return [];

  return ORDER_REQUIRED_FIELDS.filter(({ field }) => {
    if (field === 'healthInsuranceIk') return !isValidIk(order[field]);
    return !isFilled(order[field]);
  });
};

/**
 * Felder, die an einer noch ausstehenden Kassengenehmigung hängen.
 *
 * Bewusst **getrennt** von den fehlenden Pflichtangaben: Eine ausstehende
 * Genehmigung ist kein Erfassungsfehler, sondern ein laufender Vorgang bei der
 * Kasse — die Verordnung selbst kann vollständig sein, während darauf gewartet
 * wird. Der Dokumentstatus richtet sich deshalb nur nach
 * `getMissingOrderFields`; diese Felder werden lediglich hervorgehoben, damit
 * beim Eintreffen der Genehmigung klar ist, wohin sie gehört.
 */
export const getPendingApprovalFields = (order) => {
  if (!order || !needsApproval(order)) return [];

  const pending = [];
  if (order.approvalStatus !== 'approved') {
    pending.push({ field: 'approvalStatus', label: 'Genehmigung der Krankenkasse' });
  }
  if (!isFilled(order.approvalNumber)) {
    pending.push({ field: 'approvalNumber', label: 'Genehmigungsnummer' });
  }
  return pending;
};

/**
 * Zusätzliche Gründe, die kein einzelnes Feld sind — sie hängen an einem
 * Vorgang außerhalb der Verordnung (Genehmigung der Kasse) oder wurden vom
 * Fahrer gemeldet.
 */
export const getOtherIncompleteReasons = (order) => {
  if (!order) return [];
  const reasons = [];

  if (getPendingApprovalFields(order).length > 0) {
    reasons.push('Genehmigung der Krankenkasse steht noch aus — die Verordnung ist erst danach abrechenbar.');
  }

  // Vom Fahrer gemeldet: Er hat die Einsteiger-Tour gestartet, obwohl der Scan
  // Pflichtangaben nicht lesen konnte.
  if (Array.isArray(order.missingDocumentReasons)) {
    order.missingDocumentReasons
      .map(entry => String(entry).trim())
      .filter(Boolean)
      // Was ohnehin schon als fehlendes Feld auftaucht, nicht doppelt nennen.
      .filter(entry => !ORDER_REQUIRED_FIELDS.some(item => item.label.startsWith(entry)))
      .forEach(entry => reasons.push(`Vom Fahrer gemeldet: ${entry}`));
  }

  return reasons;
};
